import React, { useState } from 'react';
import { Upload, FileText, X, CheckCircle, AlertCircle, Send } from 'lucide-react';

const ProposalUpload = ({ opportunity, onClose, onSubmit }) => {
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [coverLetter, setCoverLetter] = useState('');
  const [contactInfo, setContactInfo] = useState({
    name: '',
    email: '',
    phone: '',
    company: ''
  });

  const fileCategories = [
    {
      id: 'proposal',
      label: 'Training Proposal Document',
      description: 'Complete proposal with methodology, timeline, and pricing',
      required: true,
      accept: '.pdf,.doc,.docx'
    },
    {
      id: 'resume',
      label: 'Resume/CV',
      description: 'Professional background and experience',
      required: true,
      accept: '.pdf,.doc,.docx'
    },
    {
      id: 'portfolio',
      label: 'Training Materials/Portfolio',
      description: 'Sample materials, case studies, or presentation slides',
      required: false,
      accept: '.pdf,.doc,.docx,.ppt,.pptx'
    },
    {
      id: 'certifications',
      label: 'Certifications & Credentials',
      description: 'Training certifications and professional credentials',
      required: false,
      accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png'
    },
    {
      id: 'testimonials',
      label: 'Client Testimonials/References',
      description: 'Letters of recommendation or client feedback',
      required: false,
      accept: '.pdf,.doc,.docx'
    }
  ];

  const handleFileUpload = (e, categoryId) => {
    const files = Array.from(e.target.files);
    
    files.forEach(file => {
      // Check file size (max 25MB)
      if (file.size > 25 * 1024 * 1024) {
        alert(`File "${file.name}" is too large. Maximum size is 25MB.`);
        return;
      }

      setUploadedFiles(prev => ({
        ...prev,
        [categoryId]: [...(prev[categoryId] || []), file]
      }));
    });
  };

  const removeFile = (categoryId, fileIndex) => {
    setUploadedFiles(prev => ({
      ...prev,
      [categoryId]: prev[categoryId].filter((_, index) => index !== fileIndex)
    }));
  };

  const handleContactInfoChange = (field, value) => {
    setContactInfo(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate required fields
    if (!contactInfo.name || !contactInfo.email) {
      alert('Please provide your name and email address.');
      return;
    }

    // Validate required files
    const requiredCategories = fileCategories.filter(cat => cat.required);
    const missingRequired = requiredCategories.filter(cat => 
      !uploadedFiles[cat.id] || uploadedFiles[cat.id].length === 0
    );

    if (missingRequired.length > 0) {
      alert(`Please upload the following required documents: ${missingRequired.map(cat => cat.label).join(', ')}`);
      return;
    }

    // Submit the proposal
    const submissionData = {
      opportunity,
      contactInfo,
      coverLetter,
      files: uploadedFiles,
      submissionType: 'upload'
    };

    onSubmit(submissionData);
  };

  const renderFileCategory = (category) => {
    const files = uploadedFiles[category.id] || [];

    return (
      <div key={category.id} className="border border-gray-200 rounded-lg p-4">
        <div className="mb-3">
          <h4 className="font-medium text-gray-900 flex items-center">
            {category.label}
            {category.required && <span className="text-red-500 ml-1">*</span>}
          </h4>
          <p className="text-sm text-gray-600 mt-1">{category.description}</p>
        </div>

        {/* Upload Area */}
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors mb-3">
          <input
            type="file"
            id={`upload-${category.id}`}
            className="hidden"
            accept={category.accept}
            multiple={category.id !== 'proposal' && category.id !== 'resume'}
            onChange={(e) => handleFileUpload(e, category.id)}
          />
          <label htmlFor={`upload-${category.id}`} className="cursor-pointer">
            <Upload className="mx-auto h-8 w-8 text-gray-400 mb-2" />
            <p className="text-sm text-gray-600">
              Click to upload or drag and drop
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {category.accept.replace(/\./g, '').toUpperCase()} files (max 25MB each)
            </p>
          </label>
        </div>

        {/* Uploaded Files */}
        {files.length > 0 && (
          <div className="space-y-2">
            {files.map((file, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center">
                  <FileText className="h-5 w-5 text-green-600 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{file.name}</p>
                    <p className="text-xs text-gray-500">{(file.size / (1024 * 1024)).toFixed(2)} MB</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeFile(category.id, index)}
                  className="text-red-600 hover:text-red-800 text-sm font-medium"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const getTotalFiles = () => {
    return Object.values(uploadedFiles).reduce((total, files) => total + files.length, 0);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] w-full overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Upload Proposal</h2>
            <p className="text-gray-600">For: {opportunity?.title}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-6">
            {/* Contact Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={contactInfo.name}
                    onChange={(e) => handleContactInfoChange('name', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Your full name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={contactInfo.email}
                    onChange={(e) => handleContactInfoChange('email', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="your.email@example.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={contactInfo.phone}
                    onChange={(e) => handleContactInfoChange('phone', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company/Organization
                  </label>
                  <input
                    type="text"
                    value={contactInfo.company}
                    onChange={(e) => handleContactInfoChange('company', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Your company name"
                  />
                </div>
              </div>
            </div>

            {/* Cover Letter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cover Letter / Introduction
              </label>
              <textarea
                value={coverLetter}
                onChange={(e) => setCoverLetter(e.target.value)}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Briefly introduce yourself and explain why you're the right fit for this training opportunity..."
              />
            </div>

            {/* File Upload Categories */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Upload Documents ({getTotalFiles()} files uploaded)
              </h3>
              <div className="space-y-4">
                {fileCategories.map(renderFileCategory)}
              </div>
            </div>

            {/* Information Box */}
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-blue-400 mr-2" />
                <div>
                  <p className="text-sm text-blue-700">
                    <strong>Tip:</strong> Make sure your proposal document includes detailed methodology, timeline, pricing, and how you'll address the specific requirements mentioned in the opportunity description.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="sticky bottom-0 bg-white border-t p-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 flex items-center"
            >
              <Send className="w-4 h-4 mr-2" />
              Submit Proposal
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProposalUpload;